import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Skeleton } from '@mantine/core';
import PropTypes from 'prop-types';
import { AlertCircle } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import AppCard from '../../common/AppCard';
import {
  useGlobalFormState,
  useMediaQueryIndex,
  useModalState
} from '../../../helpers/hooks';
import { useVendorBackPath } from '../../../helpers/vendorHelper';
import PageView from '../../common/PageView';
import BundleTypeSelect from '../bundles/BundleTypeSelect';
import VendorFixedBundleEdit from '../bundles/VendorFixedBundleEdit';
import {
  ECOM_DISCOUNT_TYPE_ENUM,
  ECOM_PRODUCT_BUNDLE_TYPE_ENUM,
  ECOM_PRODUCT_STATUS_ENUM,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import VolumeDiscountBundleEdit from '../bundles/VolumeDiscountBundleEdit';
import BuyXGetYBundleEdit from '../bundles/BuyXGetYBundleEdit';
import AppFlexbox from '../../common/AppFlexbox';
import ConfirmModal from '../../common/ConfirmModal';
import AppText from '../../common/AppText';
import { triggerNotification } from '../../../helpers/notification';

const BUNDLE_EDIT_VIEWS = {
  [ECOM_PRODUCT_BUNDLE_TYPE_ENUM.FIXED_BUNDLE]: VendorFixedBundleEdit,
  [ECOM_PRODUCT_BUNDLE_TYPE_ENUM.VOLUME_DISCOUNT]: VolumeDiscountBundleEdit,
  [ECOM_PRODUCT_BUNDLE_TYPE_ENUM.BUY_X_GET_Y]: BuyXGetYBundleEdit
};

const initialFormState = {
  name: '',
  description: '',
  fkEcomProductStatus: ECOM_PRODUCT_STATUS_ENUM.DRAFT,
  isPrivate: false,
  ecomVendorProductMedia: [],
  ecomVendorBundleDiscounts: [
    {
      fkEcomDiscountType: ECOM_DISCOUNT_TYPE_ENUM.NO_DISCOUNT,
      discountAmount: 0,
      minQuantity: 1,
      sort: 0
    }
  ],
  ecomVendorBundleProducts: []
};

const VendorBundleEditView = ({ duplicate }) => {
  const navigate = useNavigate();
  const { isTabletOrSmaller, isLaptopOrSmaller } = useMediaQueryIndex();
  const { pkEcomVendorBundle } = useParams();
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchVendorBundle,
    createVendorBundles,
    updateVendorBundle,
    deleteVendorBundles
  } = useContext(VendorContext);
  const [bundleTypeState, setBundleTypeState] = useState({fkEcomBundleType: ECOM_PRODUCT_BUNDLE_TYPE_ENUM.FIXED_BUNDLE});
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const {
    hasInitialized,
    hasUnsavedChanges,
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState,
    ConfirmDiscardModal
  } = useGlobalFormState(initialFormState, {
    confirmDiscard: true,
    containerWidth: 1100
  });

  const ViewComponent = BUNDLE_EDIT_VIEWS[bundleTypeState.fkEcomBundleType];
  const loading = !hasInitialized || state.ecomVendorProducts.loading;

  const { backPath } = useVendorBackPath(
    duplicate
      ? `/vendor/product-bundles/${pkEcomVendorBundle}`
      : `/vendor/product-bundles`
  );
  const ecomVendorBundle =
    pkEcomVendorBundle &&
    pkEcomVendorBundle ===
      state.ecomVendorBundle.value?.pkEcomVendorBundle.toString()
      ? state.ecomVendorBundle.value
      : null;

  useEffect(() => {
    if (pkEcomVendorBundle && authState.pkEcomVendor) {
      if (!ecomVendorBundle) {
        fetchVendorBundle(
          authState.pkEcomVendor,
          pkEcomVendorBundle,
          (data) => {
            setBundleTypeState((c) => ({
              ...c,
              fkEcomBundleType: data.fkEcomBundleType.toString()
            }));
          }
        );
      }
      else {
        setBundleTypeState((c) => ({
          ...c,
          fkEcomBundleType: ecomVendorBundle.fkEcomBundleType.toString()
        }));
      }
    }
  }, [pkEcomVendorBundle, authState.pkEcomVendor]);

  useEffect(() => {
    if (ecomVendorBundle) {
      const areDiscountsEnabled = false;
      resetFormState({
        name: ecomVendorBundle.name,
        description: ecomVendorBundle.description,
        fkEcomProductStatus: ecomVendorBundle.fkEcomProductStatus.toString(),
        isPrivate: ecomVendorBundle.isPrivate,
        ecomVendorProductMedia: ecomVendorBundle.ecomVendorProductMedia.map(
          (m) => ({
            ...m,
            key: m.pkEcomVendorProductMedia.toString(),
            pkEcomVendorMedia: m.fkEcomVendorMedia
          })
        ),
        ecomVendorBundleProducts: ecomVendorBundle.ecomVendorBundleProducts.map(
          (p) => ({
            key: p.uuid,
            uuid: p.uuid,
            count: p.count,
            sort: p.sort,
            name: p.name,
            previewImage: p.previewImage,
            fkEcomProductStatus: p.fkEcomProductStatus,
            minPrice: p.minPrice,
            maxPrice: p.maxPrice
          })
        ),
        ecomVendorBundleDiscounts:
          ecomVendorBundle.ecomVendorBundleDiscounts.length === 0 ||
          !areDiscountsEnabled
            ? [
                {
                  fkEcomDiscountType: ECOM_DISCOUNT_TYPE_ENUM.NO_DISCOUNT,
                  discountAmount: 0,
                  minQuantity: 1,
                  sort: 0
                }
              ]
            : ecomVendorBundle.ecomVendorBundleDiscounts.map((d) => ({
                fkEcomDiscountType: d.fkEcomDiscountType.toString(),
                discountAmount: d.discountAmount / 100,
                minQuantity: d.minQuantity,
                sort: d.sort
              }))
      });
    }
    else {
      resetFormState({ ...initialFormState });
    }
  }, [ecomVendorBundle, duplicate]);

  const onSaveBundle = () => {
    submitFormState((formData, onErrorCallback) => {
      const bundleData = {
        name: formState.name,
        description: formState.description,
        fkEcomProductStatus: formState.fkEcomProductStatus,
        fkEcomBundleType: ECOM_PRODUCT_BUNDLE_TYPE_ENUM.FIXED_BUNDLE,
        isPrivate: formState.isPrivate,
        ecomVendorProductMedia: [
          ...formState.ecomVendorProductMedia.map((m) => ({
            fkEcomVendorMedia: m.pkEcomVendorMedia,
            sort: m.sort
          })),
          ...(ecomVendorBundle && !duplicate
            ? ecomVendorBundle.ecomVendorProductMedia
                .filter(
                  (m) =>
                    !formState.ecomVendorProductMedia.find(
                      (fm) => fm.pkEcomVendorMedia === m.fkEcomVendorMedia
                    )
                )
                .map((m) => ({
                  fkEcomVendorMedia: m.fkEcomVendorMedia,
                  deleted: true
                }))
            : [])
        ],
        ecomVendorBundleDiscounts: formState.ecomVendorBundleDiscounts.map(
          (d) => ({
            fkEcomDiscountType: d.fkEcomDiscountType,
            discountAmount: Math.round(d.discountAmount * 100),
            sort: d.sort,
            minQuantity: d.minQuantity
          })
        ),
        ecomVendorBundleProducts: formState.ecomVendorBundleProducts.map(
          (p) => ({
            count: p.count,
            sort: p.sort,
            uuid: p.uuid
          })
        )
      };

      if (ecomVendorBundle?.pkEcomVendorBundle && !duplicate) {
        updateVendorBundle(
          ecomVendorBundle.pkEcomVendorBundle,
          bundleData,
          (data) => {
            triggerNotification('Bundle updated!', 'success');
          },
          onErrorCallback
        );
      }
      else {
        createVendorBundles(
          authState.pkEcomVendor,
          { ecomVendorBundles: [bundleData] },
          (data) => {
            navigate(`/vendor/product-bundles/${data[0].pkEcomVendorBundle}`);
            triggerNotification('Bundle created!', 'success');
          },
          onErrorCallback
        );
      }
    });
  };

  return !pkEcomVendorBundle && !bundleTypeState.fkEcomBundleType ? (
    <PageView backPath={backPath} title="Select bundle type" width={1100}>
      <BundleTypeSelect
        onSelect={(value) =>
          setBundleTypeState({ ...bundleTypeState, fkEcomBundleType: value })
        }
        showUseCaseInfoCard
      />
    </PageView>
  ) : ViewComponent && (ecomVendorBundle || !pkEcomVendorBundle) ? (
    <PageView
      backPath={backPath}
      formProps={{
        withFormControls: true,
        hasUnsavedChanges,
        isSubmitting,
        ...(!!ecomVendorBundle && !duplicate
          ? {
              deleteTitle: 'Delete bundle',
              onDelete: () => {
                onOpenModal(VIEW_ACTIONS_ENUM.DELETE, ecomVendorBundle);
              }
            }
          : {}),
        onSubmit: (e) => {
          e.preventDefault();
          e.stopPropagation();
          onSaveBundle();
        }
      }}
      onBackClick={(e) => {
        if (!pkEcomVendorBundle) {
          e.preventDefault();
          setBundleTypeState({ ...bundleTypeState, fkEcomBundleType: null });
        }
      }}
      rightSection={
        !!ecomVendorBundle &&
        !duplicate && (
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            <Button
              color="dark"
              component={Link}
              disabled={isSubmitting}
              radius="md"
              size="xs"
              to={`/vendor/product-bundles/${ecomVendorBundle.pkEcomVendorBundle}/duplicate`}
              type="button"
              variant="light"
            >
              Duplicate
            </Button>
          </AppFlexbox>
        )
      }
      title={ecomVendorBundle?.name ?? `Create fixed bundle`}
      width={1100}
    >
      {duplicate && (
        <AppCard
          radius={isTabletOrSmaller ? 0 : 'md'}
          shadow="xs"
          style={{ padding: 8 }}
          withBorder
        >
          <AppFlexbox style={{ gap: 8 }}>
            <AppStack>
              <AlertCircle color="#eca70a" style={{ size: 18 }} />
            </AppStack>
            <AppText style={{ fontSize: 14 }}>
              Duplicate this bundle by making any change and clicking save.
            </AppText>
          </AppFlexbox>
        </AppCard>
      )}

      {!duplicate && ecomVendorBundle && ecomVendorBundle.ecomStore && (
        <AppCard
          radius={isTabletOrSmaller ? 0 : 'md'}
          shadow="xs"
          style={{ padding: 0 }}
          withBorder
        >
          <AppFlexbox
            style={{
              padding: 8,
              gap: 8,
              backgroundColor: 'dodgerblue',
              color: '#fff',
              alignItems: 'center'
            }}
          >
            <AppStack>
              <AlertCircle size={18} />
            </AppStack>
            <AppText style={{ fontSize: 14 }}>Store bundle</AppText>
          </AppFlexbox>
          <AppStack style={{ padding: 8, gap: 5 }}>
            <AppText style={{ fontSize: 14 }}>
              This bundle was created by{' '}
              <span style={{ fontWeight: 500 }}>
                {ecomVendorBundle.ecomStore.name}
              </span>{' '}
              and may contain products from multiple vendors.
            </AppText>
            <AppText
              style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
            >
              To add products not from this vendor, please edit the bundle from
              the store product bundles page.
            </AppText>
          </AppStack>
        </AppCard>
      )}

      <ViewComponent
        backPath={backPath}
        duplicate={duplicate}
        formState={formState}
        isSubmitting={isSubmitting}
        loading={loading}
        productUrl={
          ecomVendorBundle?.vendorProductUuid
            ? `/vendor/products/${ecomVendorBundle.vendorProductUuid}`
            : null
        }
        setFormState={setFormState}
      />

      {ConfirmDiscardModal}

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, delete bundle"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 12 }}>
            <AppText style={{ fontSize: 14 }}>
              Are you sure you want to delete{' '}
              <span style={{ fontWeight: 500 }}>{modalState.item?.name}</span>?
            </AppText>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              This action can’t be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteVendorBundles(
            authState.pkEcomVendor,
            [modalState.item.pkEcomVendorBundle],
            () => {
              triggerNotification('Bundle deleted!', 'success');
              navigate('/vendor/product-bundles');
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${modalState.item?.name}?`}
      />
    </PageView>
  ) : (
    <PageView backPath={backPath} loading width={1100}>
      <AppFlexbox
        style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}
      >
        <AppStack style={{ flex: 2 }}>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
        </AppStack>
        <AppStack style={{ flex: 1 }}>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
        </AppStack>
      </AppFlexbox>
    </PageView>
  );
};

VendorBundleEditView.propTypes = { duplicate: PropTypes.bool };

export default VendorBundleEditView;
